
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useBranding } from '@/hooks/use-branding';
import { hexToHSL, adjustColorBrightness, getContrastColor } from '@/lib/utils';

type BrandingContextType = {
  logo: string | null;
  primaryColor: string;
  isLoading: boolean;
  refreshBranding: () => Promise<void>;
};

const defaultBrandingContext: BrandingContextType = {
  logo: null,
  primaryColor: '#0448ff', // Default blue color
  isLoading: true,
  refreshBranding: async () => {},
};

const BrandingContext = createContext<BrandingContextType>(defaultBrandingContext);

export function useBrandingContext() {
  return useContext(BrandingContext);
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { logoUrl, primaryColor, isLoading, fetchBranding } = useBranding();
  const [cssVars, setCssVars] = useState<Record<string, string>>({});

  useEffect(() => {
    // Use the brand color or default to blue
    const colorToUse = primaryColor || '#0448ff';
    
    // Calculate variations of colors for other UI elements
    const hoverColor = adjustColorBrightness(colorToUse, -10);
    const lightColor = adjustColorBrightness(colorToUse, 90);
    const veryLightColor = adjustColorBrightness(colorToUse, 95);
    
    // Get appropriate contrast colors for text
    const foregroundColor = getContrastColor(colorToUse);
    const accentForegroundColor = getContrastColor(veryLightColor);
    
    // Update CSS variables
    const newVars = {
      '--primary': colorToUse,
      '--primary-foreground': foregroundColor,
      '--ring': colorToUse,
      '--sidebar-primary': colorToUse,
      '--sidebar-primary-foreground': foregroundColor,
      '--sidebar-hover': hoverColor,
      '--sidebar-accent': veryLightColor,
      '--sidebar-accent-foreground': accentForegroundColor,
      '--accent': veryLightColor,
      '--accent-foreground': colorToUse,
    };
    
    setCssVars(newVars);
    
    // Apply all CSS variables directly
    Object.entries(newVars).forEach(([property, value]) => {
      document.documentElement.style.setProperty(property, value);
    });
    
    // Also set the color values in the HSL format that Tailwind expects
    const hsl = hexToHSL(colorToUse);
    if (hsl) {
      document.documentElement.style.setProperty('--primary', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
      document.documentElement.style.setProperty('--ring', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
      document.documentElement.style.setProperty('--sidebar-primary', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
      
      // Convert contrast color to HSL if it's a hex
      if (foregroundColor.startsWith('#')) {
        const foregroundHsl = hexToHSL(foregroundColor);
        if (foregroundHsl) {
          document.documentElement.style.setProperty(
            '--primary-foreground', 
            `${foregroundHsl.h} ${foregroundHsl.s}% ${foregroundHsl.l}%`
          );
          document.documentElement.style.setProperty(
            '--sidebar-primary-foreground', 
            `${foregroundHsl.h} ${foregroundHsl.s}% ${foregroundHsl.l}%`
          );
        }
      }
      
      const hoverHsl = hexToHSL(hoverColor);
      if (hoverHsl) {
        document.documentElement.style.setProperty(
          '--sidebar-hover', 
          `${hoverHsl.h} ${hoverHsl.s}% ${hoverHsl.l}%`
        );
      }
      
      const lightHsl = hexToHSL(veryLightColor);
      if (lightHsl) {
        document.documentElement.style.setProperty(
          '--accent', 
          `${lightHsl.h} ${lightHsl.s}% ${lightHsl.l}%`
        );
        document.documentElement.style.setProperty(
          '--sidebar-accent', 
          `${lightHsl.h} ${lightHsl.s}% ${lightHsl.l}%`
        );
      }
    }
  }, [primaryColor]);

  const refreshBranding = async () => {
    await fetchBranding();
  };

  const value = {
    logo: logoUrl,
    primaryColor: primaryColor || '#0448ff', // Default to blue if no color
    isLoading,
    refreshBranding,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}
